import { useCallback, useEffect, useSyncExternalStore } from 'react';

// Recent search keywords per user and page ("scope"), stored by the API so they
// follow the user to other devices. The list is served instantly from memory /
// localStorage and refreshed from the API in the background (on mount and when
// the window regains focus). Adds and removes update the UI first, then sync.

export interface SearchHistoryItem {
  id: string;
  term: string;
  last_used_at: string;
}

const API_BASE = import.meta.env.VITE_API_BASE_URL;
const getHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
});

const STORAGE_PREFIX = 'search-history:v1:';
const RETENTION_MS = 90 * 24 * 60 * 60 * 1000; // same 3 months the API keeps
const MAX_ITEMS = 20;
const MAX_TERM_LENGTH = 100;
const MIN_TERM_LENGTH = 2;
const REVALIDATE_AFTER_MS = 60 * 1000;
// Re-saving the newest keyword only refreshes its date, so skip it if done recently.
const REPEAT_SAVE_AFTER_MS = 10 * 60 * 1000;

interface Entry {
  items: SearchHistoryItem[];
  fetchedAt: number;
  // Bumped on every change, so a fetch that started before a change is discarded.
  version: number;
  inflight: boolean;
  // Term keys with a save in flight; kept when a fetch result doesn't have them yet.
  pending: Map<string, number>;
}

const EMPTY: SearchHistoryItem[] = [];
const entries = new Map<string, Entry>(); // keyed by `${userId}:${scope}`
const listeners = new Map<string, Set<() => void>>();

const normalizeTerm = (raw: string) => raw.trim().replace(/\s+/g, ' ').slice(0, MAX_TERM_LENGTH);
const termKey = (term: string) => term.toLowerCase();
const timeOf = (item: SearchHistoryItem) => new Date(item.last_used_at).getTime();
const isFresh = (item: SearchHistoryItem) => Date.now() - timeOf(item) < RETENTION_MS;

const readStorage = (key: string): SearchHistoryItem[] => {
  try {
    const saved = localStorage.getItem(STORAGE_PREFIX + key);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        return parsed
          .filter(i => i && typeof i.id === 'string' && typeof i.term === 'string' && isFresh(i))
          .slice(0, MAX_ITEMS);
      }
    }
  } catch {
    // ignore malformed storage value, the API fetch will repopulate it
  }
  return EMPTY;
};

const getEntry = (key: string): Entry => {
  let entry = entries.get(key);
  if (!entry) {
    entry = { items: readStorage(key), fetchedAt: 0, version: 0, inflight: false, pending: new Map() };
    entries.set(key, entry);
  }
  return entry;
};

const setItems = (key: string, items: SearchHistoryItem[]) => {
  const entry = getEntry(key);
  entry.items = items;
  entry.version++;
  try {
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(items));
  } catch {
    // ignore storage write failures (e.g. private browsing)
  }
  listeners.get(key)?.forEach(notify => notify());
};

const deleteOnServer = (id: string) =>
  fetch(`${API_BASE}/api/v1/search-history/${id}`, { method: 'DELETE', headers: getHeaders() });

const revalidate = async (key: string, scope: string) => {
  const entry = getEntry(key);
  if (entry.inflight || Date.now() - entry.fetchedAt < REVALIDATE_AFTER_MS) return;
  entry.inflight = true;
  const startVersion = entry.version;
  try {
    const response = await fetch(`${API_BASE}/api/v1/search-history?scope=${encodeURIComponent(scope)}`, { headers: getHeaders() });
    const data = await response.json();
    // Skip if logged out meanwhile, or if the user changed the list while this was loading.
    if (!data.success || entries.get(key) !== entry || entry.version !== startVersion) return;
    entry.fetchedAt = Date.now();

    const pendingItems = entry.items.filter(i => entry.pending.has(termKey(i.term)));
    const pendingKeys = new Set(pendingItems.map(i => termKey(i.term)));
    const serverItems = (data.data as SearchHistoryItem[]).filter(i => !pendingKeys.has(termKey(i.term)));
    setItems(key, [...pendingItems, ...serverItems].sort((a, b) => timeOf(b) - timeOf(a)).slice(0, MAX_ITEMS));
  } catch (err) {
    console.error('Failed to fetch search history:', err);
  } finally {
    entry.inflight = false;
  }
};

const addTerm = async (key: string, scope: string, raw: string) => {
  const term = normalizeTerm(raw);
  if (term.length < MIN_TERM_LENGTH) return;
  const entry = getEntry(key);
  const k = termKey(term);
  const existing = entry.items.find(i => termKey(i.term) === k);
  if (existing && existing === entry.items[0] && Date.now() - timeOf(existing) < REPEAT_SAVE_AFTER_MS) return;

  const item: SearchHistoryItem = {
    id: existing?.id ?? `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    term,
    last_used_at: new Date().toISOString(),
  };
  setItems(key, [item, ...entry.items.filter(i => i !== existing)].slice(0, MAX_ITEMS));

  entry.pending.set(k, (entry.pending.get(k) || 0) + 1);
  try {
    const response = await fetch(`${API_BASE}/api/v1/search-history`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ scope, term }),
    });
    const data = await response.json();
    if (!data.success || entries.get(key) !== entry) return;
    const serverId: string = data.data.id;
    const current = entry.items.find(i => termKey(i.term) === k);
    if (!current) {
      // Removed or cleared while the save was in flight.
      deleteOnServer(serverId).catch(err => console.error('Failed to delete search history:', err));
    } else if (current.id !== serverId) {
      setItems(key, entry.items.map(i => (i === current ? { ...i, id: serverId } : i)));
    }
  } catch (err) {
    console.error('Failed to save search history:', err);
  } finally {
    const count = (entry.pending.get(k) || 1) - 1;
    if (count > 0) entry.pending.set(k, count);
    else entry.pending.delete(k);
  }
};

const removeItem = (key: string, id: string) => {
  const entry = getEntry(key);
  setItems(key, entry.items.filter(i => i.id !== id));
  // A local id has no server row yet; addTerm deletes it once its save returns.
  if (id.startsWith('local-')) return;
  deleteOnServer(id).catch(err => {
    console.error('Failed to delete search history:', err);
    entry.fetchedAt = 0;
  });
};

const clearScope = (key: string, scope: string) => {
  const entry = getEntry(key);
  setItems(key, EMPTY);
  fetch(`${API_BASE}/api/v1/search-history?scope=${encodeURIComponent(scope)}`, { method: 'DELETE', headers: getHeaders() })
    .catch(err => {
      console.error('Failed to clear search history:', err);
      entry.fetchedAt = 0;
    });
};

// Drop every cached list (memory and localStorage). Call on logout so the next
// person on this browser never sees them.
export const clearSearchHistoryCache = () => {
  entries.clear();
  try {
    Object.keys(localStorage)
      .filter(k => k.startsWith(STORAGE_PREFIX))
      .forEach(k => localStorage.removeItem(k));
  } catch {
    // ignore storage access failures
  }
  listeners.forEach(set => set.forEach(notify => notify()));
};

// `scope` names the page, e.g. 'purchase_orders' (lowercase letters, digits, underscores).
export const useSearchHistory = (scope: string, userId: string | undefined) => {
  const key = userId ? `${userId}:${scope}` : null;

  const subscribe = useCallback((notify: () => void) => {
    if (!key) return () => {};
    const set = listeners.get(key) ?? new Set<() => void>();
    listeners.set(key, set);
    set.add(notify);
    return () => { set.delete(notify); };
  }, [key]);

  const items = useSyncExternalStore(subscribe, () => (key ? getEntry(key).items : EMPTY));

  useEffect(() => {
    if (!key) return;
    const refresh = () => { revalidate(key, scope); };
    refresh();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [key, scope]);

  const add = useCallback((term: string) => { if (key) addTerm(key, scope, term); }, [key, scope]);
  const remove = useCallback((id: string) => { if (key) removeItem(key, id); }, [key]);
  const clear = useCallback(() => { if (key) clearScope(key, scope); }, [key, scope]);

  return { items, add, remove, clear };
};
